import React, { useState, useEffect } from 'react';
import { Check, X, PencilSimple } from '@phosphor-icons/react';

export interface EditableDateFieldProps {
  icon: React.ComponentType<any>;
  label: string;
  field: string;
  value: any;
  isLoading?: boolean;
  isRequired?: boolean;
  onSave: (field: string, value: any) => void;
  isSaving?: boolean;
  showSuccess?: boolean;
}

export const EditableDateField: React.FC<EditableDateFieldProps> = ({
  icon: Icon,
  label,
  field,
  value,
  isLoading = false,
  isRequired = false,
  onSave,
  isSaving = false,
  showSuccess = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(() => {
    // Convert date to YYYY-MM-DD format for input
    if (value) {
      const date = new Date(value);
      return date.toISOString().split('T')[0];
    }
    return '';
  });
  
  // Update local value when prop value changes (but not during editing)
  useEffect(() => {
    if (!isEditing) {
      if (value) {
        const date = new Date(value);
        const formattedDate = date.toISOString().split('T')[0];
        setLocalValue(formattedDate);
        console.log(`📅 DateField ${field} updated to:`, formattedDate);
      } else {
        setLocalValue('');
      }
    }
  }, [value, isEditing, field]);
  
  const startEditing = () => {
    if (value) {
      const date = new Date(value);
      const formattedDate = date.toISOString().split('T')[0];
      setLocalValue(formattedDate);
    } else {
      setLocalValue('');
    }
    setIsEditing(true);
    console.log(`✏️ Started editing date ${field} with value:`, localValue);
  };
  
  const cancelEditing = () => {
    console.log(`❌ Cancelled editing date ${field}`);
    if (value) {
      const date = new Date(value);
      setLocalValue(date.toISOString().split('T')[0]);
    } else {
      setLocalValue('');
    }
    setIsEditing(false);
  };
  
  const handleSave = () => {
    console.log(`💾 Saving date ${field} with value:`, localValue);
    onSave(field, localValue);
    setIsEditing(false);
  };
  
  const formatDisplayDate = (dateValue: any) => {
    if (!dateValue) return 'Niet ingevuld';
    try {
      const date = new Date(dateValue);
      return date.toLocaleDateString('nl-NL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Ongeldig datum';
    }
  };
  
  if (!isEditing) {
    return (
      <div className="flex items-center justify-between p-2 border-b border-neutral-primary/10 last:border-b-0">
        <div className="flex items-center gap-2">
          <Icon size={18} className="text-neutral-primary/60" />
          <span className="text-sm font-medium text-neutral-primary">
            {label}
            {isRequired && <span className="text-red-500 ml-1">*</span>}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm text-neutral-primary min-w-[100px] text-right">
            {isLoading ? 'Laden...' : formatDisplayDate(value)}
          </span>
          {showSuccess && (
            <div className="flex items-center gap-1 px-1 py-0.5 bg-status-good text-white rounded-sm text-xs">
              <Check size={10} weight="bold" />
            </div>
          )}
          <button
            onClick={startEditing}
            className="text-neutral-primary/40 hover:text-moss-primary transition-colors"
            disabled={isLoading}
          >
            <PencilSimple size={16} />
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex items-center justify-between p-2 border-b border-neutral-primary/10 last:border-b-0">
      <div className="flex items-center gap-2">
        <Icon size={18} className="text-neutral-primary/60" />
        <span className="text-sm font-medium text-neutral-primary">
          {label}
          {isRequired && <span className="text-red-500 ml-1">*</span>}
        </span>
      </div>
      
      <div className="flex items-center gap-2">
        <input
          type="date"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          className="border border-neutral-primary/20 rounded-sm px-2 py-1 text-sm focus:outline-hidden focus:border-moss-primary"
          autoFocus
        />
        <button
          type="button"
          disabled={isSaving}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSave();
          }}
          className={`transition-colors ${
            isSaving 
              ? 'text-gray-400 cursor-not-allowed' 
              : 'text-status-good hover:text-status-good/80'
          }`}
        >
          {isSaving ? (
            <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <Check size={16} weight="bold" />
          )}
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            cancelEditing();
          }}
          className="text-brick-primary hover:text-brick-primary/80 transition-colors"
        >
          <X size={16} weight="bold" />
        </button>
      </div>
    </div>
  );
};
